/**
 * 명치사 개인정보처리방침 본문.
 * - 한국 개인정보보호법 / 정보통신망법 기준 표준 구성 + 명치사 도메인(치과 종사자, 면허번호, 이력서) 반영
 * - 운영 적용 전 법무 검토 권장. 본 문구는 실무 베이스라인 초안입니다.
 */
import {
    toKoreanDate,
    WITHDRAWAL_RETENTION_EFFECTIVE_DATE,
    WITHDRAWAL_RETENTION_EFFECTIVE_ISO,
    WITHDRAWAL_RETENTION_NOTICE_DATE,
} from './withdrawalAmendmentDates';

export const PRIVACY_SERVICE_NAME = '명치사';
export const PRIVACY_DOMAIN = 'www.myungchisa.com';
export const PRIVACY_CONTACT_EMAIL = 'support@myungchisa.com';
export const PRIVACY_OFFICER_NAME = '개인정보보호책임자';
/**
 * 화면에 노출하는 "시행일 / 버전" 값.
 * 제12조가 개정 사전 고지 기간을 규정하므로, 게시일이 아니라 **개정 시행일**을 버전으로 쓴다.
 */
/**
 * 가장 늦은 개정 시행일 — 1차 개정(2026-10-12)과 6차 개정(제3조 제1항·제5항 탈퇴 후 복구 기간·익명 처리, withdrawalAmendmentDates.ts) 중 늦은 날.
 */
/**
 * 8차 개정(화면 행동 기록 — 노출·클릭 등 수집 항목 추가, 맞춤 추천 동의 범위 명시, 보관 기간, 세션 식별값) 게시일·시행일.
 * 같은 8차 개정에 AI 질문 열람(제2조 제4-1호, 제3조 제6항 — 어드민 질문 분야 예시, 개인정보 가린 질문만)도 함께 넣었다 — 고지일·시행일을 같이 쓴다.
 * 수집 항목 추가라 제12조의 7일 전 고지를 적용한다(docs/unified-log-plan.md 5.3, 결정 11). 화면 수집 스위치(어드민 기능 공개 ui_events)는
 * **시행일에** 켠다 — 그 전에는 꺼 둔 채로 배포해도 된다. 고지 배포일이 바뀌면 두 ISO 값만 고친다(시행일 = 고지일 + 7일).
 */
export const BEHAVIOR_LOG_NOTICE_ISO = '2026-10-11';
export const BEHAVIOR_LOG_EFFECTIVE_ISO = '2026-10-18';
export const BEHAVIOR_LOG_NOTICE_DATE: string = toKoreanDate(BEHAVIOR_LOG_NOTICE_ISO);
export const BEHAVIOR_LOG_EFFECTIVE_DATE: string = toKoreanDate(BEHAVIOR_LOG_EFFECTIVE_ISO);

export const PRIVACY_VERSION: string = ['2026-10-12', WITHDRAWAL_RETENTION_EFFECTIVE_ISO, BEHAVIOR_LOG_EFFECTIVE_ISO].sort().reverse()[0];
export const PRIVACY_EFFECTIVE_DATE = '2026년 6월 1일';
/** 이번 개정(면허 인증 이미지·설문 응답 항목 추가)을 서비스에 게시·고지한 날 */
export const PRIVACY_AMENDMENT_NOTICE_DATE = '2026년 9월 12일';
/** 개정 시행일 — 민감정보 수집 항목 추가는 중요한 변경이므로 제12조의 30일 고지 기간을 적용한다 */
export const PRIVACY_AMENDMENT_EFFECTIVE_DATE = '2026년 10월 12일';
/**
 * 2차 개정(면허 인증 이미지 자동 판독·자동 승인 — Anthropic 처리위탁·국외 이전, 자동화된 결정) 게시일·시행일.
 * 사용자 결정(2026-09-23)으로 30일 유예 없이 즉시 시행 — 자동 판독을 2026-09-23 부터 운영에 켰다.
 */
export const PRIVACY_OVERSEAS_NOTICE_DATE = '2026년 9월 22일';
export const PRIVACY_OVERSEAS_EFFECTIVE_DATE = '2026년 9월 23일';
/**
 * 3차 개정(제3조 제3항 면허 인증 이미지 보관 기간 — 승인 건 30일 보관·반려 건 즉시 파기) 게시일 = 시행일.
 * 사용자 결정(2026-09-28)으로 게시 즉시 시행. 배포일에 맞춰 이 값만 바꾼다.
 */
export const LICENSE_RETENTION_NOTICE_DATE = '2026년 9월 28일';
/**
 * 4차 개정(제2조 제6호·제3조 제3항 — 면허 인증 승인 시 성명·생년월일·면허번호 회원정보 반영) 게시일 = 시행일.
 * 사용자 결정(2026-10-01)으로 게시 즉시 시행. 배포일에 맞춰 이 값만 바꾼다.
 */
export const LICENSE_ACCOUNT_SYNC_NOTICE_DATE = '2026년 10월 1일';
/**
 * 5차 개정(AI 질문 — 제1조 제2호, 제2조 제4-1호, 제3조 제6항, 제5조·제5조의2 Anthropic 위탁·국외 이전 항목 추가) 게시일·시행일.
 * AI 질문은 첫 사용 때 별도 동의를 받고 쓰는 기능이라, 면허 자동 판독(2차 개정) 때와 같이 게시 즉시 시행하는 것을 기본으로 둔다.
 * 기능 스위치(AI_CHAT_ENABLED)를 켜는 배포일에 맞춰 이 두 값만 바꾼다 — 스위치를 켜기 전에 이 개정이 게시돼 있어야 한다.
 */
export const AI_CHAT_NOTICE_DATE = '2026년 10월 3일';
export const AI_CHAT_EFFECTIVE_DATE = '2026년 10월 3일';
/**
 * 7차 개정(홈페이지가 없는 병·의원 회원의 사업자등록증 이미지 — 제1조 제3호, 제2조 제1-1호, 제3조 제7항, 제5조·제5조의2·제7조의2 항목 추가) 게시일·시행일.
 * 사업자등록증 사진 칸을 여는 사용자 웹 배포일에 바로 시행(2026-10-10 탈퇴 개정과 같은 날 — 시행일·버전은 그대로).
 */
export const BUSINESS_CERT_NOTICE_DATE: string = WITHDRAWAL_RETENTION_NOTICE_DATE;
export const BUSINESS_CERT_EFFECTIVE_DATE: string = WITHDRAWAL_RETENTION_EFFECTIVE_DATE;

export const PRIVACY_POLICY: string = `
${PRIVACY_SERVICE_NAME}(이하 "회사")는 「개인정보 보호법」 및 「정보통신망 이용촉진 및 정보보호 등에 관한 법률」 등 관련 법령을 준수하며, 이용자의 개인정보를 보호하고 권익을 존중하기 위해 다음과 같이 개인정보처리방침을 수립·공개합니다.

제1조 (개인정보의 수집 항목 및 방법)
회사는 회원가입, 서비스 제공, 채용·세미나·커뮤니티 운영을 위해 다음의 개인정보를 수집합니다.

1. 회원가입 시 수집 항목
  - (공통/필수) 이메일(로그인 아이디), 비밀번호, 이름, 휴대전화번호, 닉네임
  - (일반회원) 직군, 생년월일, 성별, 거주지역, 경력 정보
  - (병·의원 회원) 치과/사업자명, 대표자명, 담당자명·담당자 이메일·연락처, 병원 주소, 사업자/요양기관 정보

2. 서비스 이용 과정에서 수집되는 항목
  - 이력서·프로필: 학력, 경력, 자격·면허 정보, 희망 근무조건, 자기소개, 첨부파일
  - 채용 지원·관리 내역, 세미나 신청·참가 내역, 커뮤니티 게시물·댓글
  - 결제·포인트 이용 시: 결제내역(결제수단 정보는 PG사가 보관)
  - AI 질문 이용 시: 회원이 입력한 질문과 AI 답변을 포함한 대화 내용, 답변 평가(도움 됐어요·별로예요·신고 및 회원이 적은 사유), 치과의사·치과 회원이 답변 아래 객관식 질문에 고른 답, 이용 기록(질문 시각, 질문 수·질문권 지급 및 사용 기록), AI 질문 이용 동의 기록

3. 민감정보(면허·자격) 수집
  - 치과위생사·치과기공사·치과의사 등 면허/자격번호 및 인증 정보 (별도 동의 시)
  - 면허 인증 신청 시: 면허증(또는 면허증명서) 이미지, 성명, 생년월일, 면허번호 (회원이 신청하고 별도 동의한 경우에 한함)
  - 홈페이지가 없는 병·의원 회원의 가입·승인 시: 사업자등록증 이미지 및 이미지에 기재된 상호, 대표자명, 사업자등록번호, 사업장 소재지, 개업일, 업태·종목 (홈페이지 대신 승인 확인 자료로 제출한 경우에 한함)

3-1. 설문 응답 수집
  - 급여 실태조사 등 설문에 응답한 경우: 직군, 경력, 근무 지역, 근무 조건, 급여 구간 등 회원이 입력한 응답 내용

4. 자동으로 생성·수집되는 정보
  - 접속 IP, 쿠키, 서비스 이용기록, 접속 로그, 기기정보(OS·브라우저), 부정이용 기록
  - 방문 페이지·조회, 화면에 표시된 콘텐츠와 누른 항목(노출·클릭), 화면 이용 시간(화면별로 실제 보인 시간), 목록 조건 선택, 검색 이용 여부·결과 수, AI 질문 버튼·추천 질문 이용 등 서비스 이용 기록(행동 이벤트), 방문 단위 구분값(세션 식별값), 앱 버전 및 비로그인 이용자 구분을 위한 분석용 기기 식별값(브라우저 로컬 저장값 device_id). 입력한 글의 내용과 검색어 원문은 행동 이벤트로 수집하지 않습니다.

5. 수집 방법
  - 회원가입 및 서비스 이용 과정에서 이용자가 직접 입력
  - 서비스 이용 중 자동 생성·수집 도구를 통한 수집

제2조 (개인정보의 수집·이용 목적)
회사는 수집한 개인정보를 다음의 목적으로만 이용합니다.
1. 회원 가입·관리, 본인확인, 부정이용 방지
1-1. 병·의원 회원 승인 확인 (사업자등록번호·대표자명·개업일로 국세청 사업자등록 상태·진위 확인, 건강보험심사평가원 공개 병원정보와 치과명·주소·전화 대조, 홈페이지 또는 사업자등록증 이미지에 기재된 사항과 입력값 대조 및 그에 따른 자동 승인. 사업자등록증 이미지는 오직 승인 확인에만 이용합니다. 자동 승인의 기준은 제7조의2 참조)
2. 채용공고 게시·검색·지원 및 지원자-병의원 간 매칭 서비스 제공
3. 세미나 등록·신청·참가 관리
4. 전문가 라운지·속닥속닥 커뮤니티 운영
4-1. AI 질문·답변 서비스 제공(답변 생성, 질문 분야 분류, 명치사에 공개된 자료(급여 리포트·전문가 글 등) 안내, 전문가 라운지·속닥속닥 글 초안 작성, 질문 수 관리) 및 답변 품질 개선(대화 내용을 개인을 알아볼 수 없게 정리한 질문·답변 요지를 내부 참고 자료로 활용, 개인을 알아볼 수 있는 정보를 가린 질문의 열람으로 자주 묻는 주제 파악), 객관식 질문에 고른 답을 이름 없이 모아 다른 회원 안내 자료로 활용
5. 이력서·프로필 작성 및 (동의 시) 병의원 열람 제공
6. 면허·자격 인증을 통한 신뢰성 확보 (면허 인증 이미지는 오직 면허 진위 확인 및 인증 배지 부여에만 이용하며, 이미지 기재 사항의 자동 판독과 그에 따른 자동 승인을 포함합니다. 자동 승인의 기준은 제7조의2 참조. 승인 시 신청한 성명·생년월일·면허번호로 회원정보를 정정합니다)
6-1. 급여 실태조사 등 설문 응답의 비식별 통계 분석 및 리포트 발행 (개인 응답은 공개하지 않으며, 응답 수가 적은 그룹은 표시하지 않습니다)
7. 공지사항 전달, 문의 응대, 분쟁 처리
8. (맞춤 추천 알고리즘 활용에 동의한 경우) 서비스 이용 기록으로 관심 지역·역·치과·채용 형태를 요약하여 홈 화면 구성, AI 질문 추천 문구, 채용·세미나·콘텐츠 추천에 이용
8-1. (동의 시) 마케팅·이벤트 정보 제공
9. 서비스 개선, 통계 분석(방문자 수, 화면별 노출 대비 클릭 등 기능 이용 통계), 보안 및 안정적 운영

제3조 (개인정보의 보유 및 이용기간)
1. 회사는 원칙적으로 수집·이용 목적이 달성되면 개인정보를 지체 없이 파기합니다. 회원 탈퇴 시의 보관과 익명 처리는 제5항에 따릅니다.
2. 다만 관계 법령에 따라 보존이 필요한 경우 아래 기간 동안 보관합니다.
  - 계약 또는 청약철회 등에 관한 기록: 5년 (전자상거래법)
  - 대금결제 및 재화 등의 공급에 관한 기록: 5년 (전자상거래법)
  - 소비자의 불만 또는 분쟁처리에 관한 기록: 3년 (전자상거래법)
  - 로그인 기록(접속 로그): 3개월 (통신비밀보호법)
3. 면허 인증 이미지는 승인된 건은 사후 확인을 위해 승인일로부터 30일간 보관한 뒤 파기하고(관리자가 확인을 마치면 그 전에도 파기), 반려된 건은 반려 즉시 파기합니다. 검수 전 30일이 경과한 신청은 자동 파기합니다. 승인 시 성명·생년월일·면허번호를 회원정보에 반영하며, 파기 후에는 인증 결과·면허번호·검수 이력만 보관하고, 회원 탈퇴 시 삭제합니다.
4. 설문 응답은 비식별 통계로 처리한 뒤 집계 결과만 보관하며, 개인 식별이 가능한 원본 응답은 회원 탈퇴 시 삭제합니다.
5. 회원이 탈퇴하면 30일 동안 계정 복구를 위해 회원정보를 보관하며, 이 기간에는 로그인과 서비스 이용이 제한됩니다. 이 기간 안에 같은 계정으로 로그인하면 계정을 복구할 수 있습니다. 30일이 지나면 이름·닉네임·이메일·휴대전화번호·생년월일·주소·면허번호·소셜 계정 연결 정보·기기 알림 정보 등 개인을 알아볼 수 있는 정보를 지체 없이 익명 처리합니다. 면허 인증 신청 정보, 설문 원본 응답, AI 질문 대화는 탈퇴 즉시 파기하며 복구되지 않습니다. 회원이 작성한 게시글·댓글(작성 당시 닉네임 포함)과 병·의원에 제출한 지원서·이력서는 탈퇴 후에도 삭제되지 않습니다.
6. AI 질문 대화(질문·답변·평가)는 회원이 대화를 삭제하거나 탈퇴할 때까지 보관하며, 삭제·탈퇴 시 지체 없이 대화 내용을 파기합니다. 질문 수·질문권 기록과 AI 질문 이용 동의 기록은 회원 탈퇴 시 파기합니다. 대화를 개인을 알아볼 수 없게 정리한 내부 참고 자료(질문·답변 요지)는 특정 개인을 알아볼 수 없는 정보로서 대화 삭제·탈퇴 후에도 보관할 수 있습니다. 회원이 공유한 AI 대화는 링크를 받은 사람이 볼 수 있으며, 받은 회원이 이어서 질문하면 그 대화 내용이 질문 맥락으로 함께 전달됩니다. 공유 전 개인정보로 보이는 내용을 안내하며, 공유는 언제든 중지할 수 있습니다. 회사는 AI 답변 품질 개선과 회원이 자주 묻는 주제를 파악하기 위해, 회원이 명치사 AI에 보낸 질문을 이름·연락처·치과명 등 개인을 알아볼 수 있는 정보를 가린 상태로 열람할 수 있습니다. 이때 질문한 회원의 계정 정보는 함께 보지 않으며, 회원이 삭제한 대화는 열람하지 않습니다.
7. 사업자등록증 이미지는 제출일로부터 30일간 보관한 뒤 파기합니다. 가입·저장에 쓰이지 않은 이미지는 24시간 뒤 파기합니다. 이미지에서 읽은 값과 대조 결과는 승인 이력으로 보관하고, 회원 탈퇴 시 이미지와 함께 삭제합니다.
8. 서비스 이용 기록(행동 이벤트)은 수집일로부터 90일(화면 노출 기록은 14일)이 지나면 자동 파기합니다. 이용 기록으로 만든 관심 요약은 맞춤 추천 동의를 철회하거나 탈퇴하면 지체 없이 파기합니다. 회원이 탈퇴하면 제5항의 복구 기간(30일)이 지난 뒤 이용 기록에서 회원번호와 기기 식별값을 지체 없이 지우며, 남은 기록은 특정 개인을 알아볼 수 없는 통계 목적으로만 보관 기간까지 이용합니다. 개인을 알아볼 수 없게 합산한 일별 통계는 2년간 보관합니다.

제4조 (개인정보의 제3자 제공)
1. 회사는 이용자의 개인정보를 본 방침에 명시한 범위를 넘어 제3자에게 제공하지 않습니다.
2. 다만 다음의 경우 예외로 합니다.
  - 이용자가 사전에 동의한 경우 (예: 채용 지원 시 지원자의 이력서·프로필이 해당 병·의원 회원에게 제공)
  - 법령에 의하거나 수사기관이 적법한 절차에 따라 요청한 경우

제5조 (개인정보 처리의 위탁)
회사는 원활한 서비스 제공을 위해 다음과 같이 개인정보 처리를 위탁할 수 있으며, 위탁계약 시 개인정보가 안전하게 관리되도록 필요한 사항을 규정합니다.
  - 클라우드 인프라 운영: Amazon Web Services (데이터 저장·호스팅)
  - 이메일 발송: Amazon Simple Email Service (인증·안내 메일 발송)
  - 결제 처리(해당 시): 제휴 전자결제대행(PG) 사업자
  - 면허 인증 이미지 자동 판독: Anthropic, PBC (면허증 이미지에 기재된 성명·생년월일·면허번호의 인식. 국외 이전 사항은 제5조의2 참조)
  - 사업자등록증 이미지 자동 판독: Anthropic, PBC (사업자등록증 이미지에 기재된 상호·대표자명·사업자등록번호·사업장 소재지 등의 인식. 국외 이전 사항은 제5조의2 참조)
  - AI 질문·답변 생성: Anthropic, PBC (회원이 입력한 질문에 대한 답변 생성, 질문 분류, 글 초안 작성. 국외 이전 사항은 제5조의2 참조)

제5조의2 (개인정보의 국외 이전)
회사는 「개인정보 보호법」 제28조의8 제1항 제3호에 따라 개인정보 처리를 위탁하면서 아래와 같이 개인정보를 국외로 이전합니다.
  - 이전받는 자: Anthropic, PBC (연락처: privacy@anthropic.com)
  - 이전되는 국가: 미국
  - 이전되는 개인정보 항목: 면허 인증 신청 시 제출한 면허증(또는 면허증명서) 이미지 및 이미지에 기재된 정보(성명, 생년월일, 면허번호 등). 회원이 신청서에 입력한 값은 이전하지 않습니다.
  - 이전 시기 및 방법: 면허 인증 신청 접수 후 자동 판독 시(통상 접수 후 수 분 이내), 암호화된 통신 구간(HTTPS)을 통해 전송
  - 이전받는 자의 이용 목적: 면허증 이미지에 기재된 사항의 인식(판독) 결과를 회사에 회신하는 것에 한하며, 인공지능 모델 학습에 이용하지 않습니다.
  - 이전받는 자의 보유·이용 기간: 판독 완료 후 이전받는 자의 데이터 보관 정책에 따라 전송일로부터 30일 이내 삭제 (법령 준수 또는 이용정책 위반 조사를 위해 필요한 경우 예외)
  - 이전을 거부하는 방법·절차 및 거부의 효과: 면허 인증 신청 화면의 동의 항목 '자세히'에서 '자동 판독 없이 관리자가 직접 확인해 주세요'를 선택하면 해당 신청은 국외로 이전하지 않고 관리자가 직접 검수합니다. 거부하더라도 면허 인증 이용에는 제한이 없으나 인증까지 시간이 더 걸릴 수 있습니다.

사업자등록증 이미지 자동 판독 시 국외 이전
  - 이전받는 자: Anthropic, PBC (연락처: privacy@anthropic.com)
  - 이전되는 국가: 미국
  - 이전되는 개인정보 항목: 홈페이지가 없는 병·의원 회원이 제출한 사업자등록증 이미지 및 이미지에 기재된 정보(상호, 대표자명, 사업자등록번호, 사업장 소재지 등). 회원이 입력한 값은 이전하지 않습니다.
  - 이전 시기 및 방법: 이미지 제출 후 자동 판독 시(통상 수 분 이내), 암호화된 통신 구간(HTTPS)을 통해 전송
  - 이전받는 자의 이용 목적: 이미지에 기재된 사항의 인식(판독) 결과를 회사에 회신하는 것에 한하며, 인공지능 모델 학습에 이용하지 않습니다.
  - 이전받는 자의 보유·이용 기간: 판독 완료 후 이전받는 자의 데이터 보관 정책에 따라 전송일로부터 30일 이내 삭제 (법령 준수 또는 이용정책 위반 조사를 위해 필요한 경우 예외)
  - 이전을 거부하는 방법·절차 및 거부의 효과: 사업자등록증 대신 치과 홈페이지 주소로 승인을 받으면 국외로 이전하지 않습니다. 이미 제출한 경우에도 ${PRIVACY_CONTACT_EMAIL} 로 요청하면 자동 판독 없이 관리자가 직접 확인합니다. 거부하더라도 서비스 이용에는 제한이 없으나 승인까지 시간이 더 걸릴 수 있습니다.

AI 질문 이용 시 국외 이전
  - 이전받는 자: Anthropic, PBC (연락처: privacy@anthropic.com)
  - 이전되는 국가: 미국
  - 이전되는 개인정보 항목: 회원이 AI 질문에 입력한 질문과 같은 대화의 앞선 질문·답변 내용(전문가 라운지·속닥속닥 글 초안을 요청한 경우 그 대화 내용 포함). 회원 번호·이름·연락처·이메일 등 계정 정보는 이전하지 않습니다.
  - 치과 회원이 받은 지원서를 AI로 요약할 때 지원자의 이름·연락처·생년월일·면허번호는 가린 뒤 처리합니다.
  - 이전 시기 및 방법: 회원이 질문을 보내거나 글 초안을 요청할 때마다 즉시, 암호화된 통신 구간(HTTPS)을 통해 전송
  - 이전받는 자의 이용 목적: 답변·질문 분류·글 초안을 만들어 회사에 회신하는 것에 한하며, 인공지능 모델 학습에 이용하지 않습니다.
  - 이전받는 자의 보유·이용 기간: 처리 완료 후 이전받는 자의 데이터 보관 정책에 따라 전송일로부터 30일 이내 삭제 (법령 준수 또는 이용정책 위반 조사를 위해 필요한 경우 예외)
  - 이전을 거부하는 방법·절차 및 거부의 효과: AI 질문을 처음 이용할 때 표시되는 동의 창에서 동의하지 않으면 국외로 이전하지 않습니다. 거부하면 AI 질문을 이용할 수 없으나, 그 밖의 서비스 이용에는 제한이 없습니다. 이미 동의한 경우에도 ${PRIVACY_CONTACT_EMAIL} 로 동의 철회를 요청할 수 있습니다.

제6조 (개인정보의 파기 절차 및 방법)
1. 파기 절차: 보유기간이 경과하거나 처리 목적이 달성된 개인정보는 내부 방침 및 관계 법령에 따라 파기합니다.
2. 파기 방법: 전자적 파일은 복구 불가능한 방법으로 영구 삭제하며, 종이 문서는 분쇄 또는 소각합니다.

제7조 (정보주체의 권리·의무 및 행사방법)
1. 이용자는 언제든지 자신의 개인정보에 대한 열람·정정·삭제·처리정지를 요구할 수 있습니다.
2. 권리 행사는 서비스 내 '내 정보' 메뉴 또는 ${PRIVACY_CONTACT_EMAIL} 을 통해 요청할 수 있으며, 회사는 지체 없이 조치합니다.
3. 만 14세 미만 아동의 개인정보는 수집하지 않습니다.

제7조의2 (자동화된 결정)
회사는 「개인정보 보호법」 제37조의2에 따라 면허 인증에서 다음과 같이 자동화된 결정을 합니다.
  - 대상: 면허 인증 신청의 승인
  - 기준: 제출한 이미지가 치과위생사 면허증 또는 면허(자격)증명서로 판독되고, 이미지에 기재된 성명·생년월일·면허번호가 신청 정보와 모두 일치하며, 편집·합성 흔적이 보이지 않는 경우 자동으로 승인합니다.
  - 절차: 신청 접수 후 이미지를 자동 판독(제5조의2의 국외 이전 포함)하여 위 기준을 충족하면 즉시 승인하고, 하나라도 충족하지 않으면 관리자가 직접 검수합니다. 반려는 자동으로 하지 않으며 항상 관리자가 결정합니다. 자동 승인된 신청은 사람이 사후에 확인할 수 있도록 이미지를 승인일로부터 30일간 보관한 뒤 파기합니다(제3조 제3항).
  - 정보주체의 권리: 자동 승인 결과에 대해 설명 또는 관리자 재검토를 ${PRIVACY_CONTACT_EMAIL} 로 요구할 수 있으며, 회사는 지체 없이 조치합니다. 자동화된 결정을 원하지 않는 경우 신청 시 제5조의2의 방법으로 자동 판독 제외를 선택하면 관리자가 직접 검수합니다.

병·의원 회원 승인에서도 다음과 같이 자동화된 결정을 합니다.
  - 대상: 병·의원 회원 가입의 승인
  - 기준: 필수 정보를 모두 입력하고, ① 국세청에서 사업자등록번호·대표자명·개업일이 일치하고 계속사업자로 확인되며 건강보험심사평가원 공개 병원정보에 같은 치과(이름·주소)가 있는 경우, ② 치과 홈페이지에 적힌 대표자·사업자등록번호·주소가 입력값과 모두 일치하는 경우, 또는 ③ 사업자등록증 이미지에 기재된 대표자·사업자등록번호·주소가 입력값과 모두 일치하는 경우 자동으로 승인합니다. 휴업·폐업 사업자, 같은 사업자등록번호로 가입한 다른 회원이 있는 경우, 이전에 승인되지 않은 이력이 있는 경우는 자동으로 승인하지 않습니다.
  - 절차: 기준을 충족하지 않으면 관리자가 직접 확인합니다. 승인 거절은 자동으로 하지 않으며 항상 관리자가 결정합니다.
  - 정보주체의 권리: 자동 승인 결과에 대해 설명 또는 관리자 재검토를 ${PRIVACY_CONTACT_EMAIL} 로 요구할 수 있습니다.

제8조 (개인정보의 안전성 확보조치)
회사는 개인정보의 안전한 처리를 위해 다음의 조치를 취합니다.
  - 비밀번호 암호화 저장(단방향 암호화) 및 통신구간 암호화(SSL/TLS)
  - 개인정보 접근권한의 최소화 및 접근통제
  - 접속기록의 보관 및 위·변조 방지
  - 보안 프로그램 설치 및 주기적 점검

제9조 (쿠키의 운영)
1. 회사는 맞춤형 서비스 제공을 위해 쿠키(cookie)를 사용할 수 있습니다.
2. 회사는 서비스 개선을 위한 이용 통계(방문자 수·인기 콘텐츠 등)를 산출하기 위해 브라우저 로컬 저장소에 분석용 기기 식별값(device_id)을 저장합니다. 이는 비로그인 이용자를 통계상 구분하기 위한 임의의 값이며, 이름·연락처 등 개인을 특정하는 정보를 담지 않습니다. 또한 한 번의 방문 안에서 이용 흐름을 통계로 구분하기 위해 브라우저 세션 저장소에 임의의 세션 식별값을 저장하며, 브라우저를 닫거나 30분간 이용이 없으면 새 값으로 바뀝니다.
3. 이용자는 웹브라우저 설정을 통해 쿠키·로컬 저장소 데이터를 삭제·거부할 수 있습니다. 분석용 식별값은 필수 항목이 아니므로 거부하여도 서비스 이용에는 제한이 없으나, 로그인 유지 등 일부 필수 쿠키를 거부하는 경우 해당 서비스 이용에 제한이 있을 수 있습니다.

제10조 (개인정보보호책임자)
회사는 개인정보 처리에 관한 업무를 총괄하고 이용자의 문의·불만을 처리하기 위해 아래와 같이 개인정보보호책임자를 지정합니다.
  - 개인정보보호책임자: ${PRIVACY_OFFICER_NAME}
  - 문의: ${PRIVACY_CONTACT_EMAIL}

제11조 (권익침해 구제방법)
개인정보 침해로 인한 신고·상담이 필요한 경우 아래 기관에 문의할 수 있습니다.
  - 개인정보분쟁조정위원회: 1833-6972 (www.kopico.go.kr)
  - 개인정보침해신고센터: 118 (privacy.kisa.or.kr)
  - 대검찰청 사이버수사과: 1301 (www.spo.go.kr)
  - 경찰청 사이버수사국: 182 (cyberbureau.police.go.kr)

제12조 (개인정보처리방침의 변경)
본 방침은 법령·정책 또는 서비스 변경에 따라 개정될 수 있으며, 변경 시 시행 7일 전(중요한 변경은 30일 전)부터 서비스 내 공지사항을 통해 고지합니다.

부칙
본 개인정보처리방침은 ${PRIVACY_EFFECTIVE_DATE}부터 시행합니다.
본 개정 방침(면허 인증 이미지·설문 응답 처리 항목 추가)은 ${PRIVACY_AMENDMENT_NOTICE_DATE}에 공지하였으며, 제12조의 사전 고지 기간(중요한 변경 30일 전)에 따라 ${PRIVACY_AMENDMENT_EFFECTIVE_DATE}부터 시행합니다. 시행일 전까지는 종전 방침이 적용됩니다.
면허 인증 이미지 자동 판독·자동 승인에 관한 개정(제2조 제6호, 제3조 제3항 보관기간, 제5조 수탁자 추가, 제5조의2·제7조의2 신설)은 ${PRIVACY_OVERSEAS_NOTICE_DATE}에 본 방침을 통해 게시하였으며, ${PRIVACY_OVERSEAS_EFFECTIVE_DATE}부터 시행합니다.
제3조 제3항(면허 인증 이미지 보관 기간) 개정은 ${LICENSE_RETENTION_NOTICE_DATE}에 게시하고 같은 날부터 시행합니다.
면허 인증 승인 시 회원정보 반영에 관한 개정(제2조 제6호, 제3조 제3항)은 ${LICENSE_ACCOUNT_SYNC_NOTICE_DATE}에 게시하고 같은 날부터 시행합니다.
AI 질문 서비스에 관한 개정(제1조 제2호, 제2조 제4-1호, 제3조 제6항, 제5조 수탁자 업무 추가, 제5조의2 AI 질문 국외 이전 신설)은 ${AI_CHAT_NOTICE_DATE}에 게시하고 ${AI_CHAT_EFFECTIVE_DATE}부터 시행합니다.
병·의원 회원 승인 확인(사업자등록증 이미지·국세청·건강보험심사평가원 조회)에 관한 개정(제1조 제3호, 제2조 제1-1호, 제3조 제7항, 제5조 수탁자 업무 추가, 제5조의2 사업자등록증 국외 이전·제7조의2 신설)은 ${BUSINESS_CERT_NOTICE_DATE}에 게시하고 ${BUSINESS_CERT_EFFECTIVE_DATE}부터 시행합니다.
회원 탈퇴 후 보관·익명 처리에 관한 개정(제3조 제1항·제5항)은 ${WITHDRAWAL_RETENTION_NOTICE_DATE}에 게시하고 ${WITHDRAWAL_RETENTION_EFFECTIVE_DATE}부터 시행합니다.
서비스 이용 기록(행동 이벤트)에 관한 개정(제1조 제4호, 제2조 제8호·제8-1호·제9호, 제3조 제8항, 제9조 제2항)은 ${BEHAVIOR_LOG_NOTICE_DATE}에 게시하였으며, 제12조의 사전 고지 기간(7일 전)에 따라 ${BEHAVIOR_LOG_EFFECTIVE_DATE}부터 시행합니다. 시행일 전까지는 종전 방침이 적용됩니다.
AI 질문 열람(개인을 알아볼 수 있는 정보를 가린 질문)에 관한 개정(제2조 제4-1호, 제3조 제6항)은 ${BEHAVIOR_LOG_NOTICE_DATE}에 게시하였으며, 제12조의 사전 고지 기간(7일 전)에 따라 ${BEHAVIOR_LOG_EFFECTIVE_DATE}부터 시행합니다. 시행일 전까지는 종전 방침이 적용됩니다.
`.trim();
